// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import EditableField from "../../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";

// HELPERS
import {createClassName, capitalizeFirstLetter} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./CustomerInformation.fulfillment";
import Button from "../../../../../components/Button/Button";
import api from "../../../../../helpers/api";
import CompanyReps from "../../../components/CompanyReps/CompanyReps";
import i18n from "@helpers/i18n";
import Settings from "@classes/Settings";

export default class CobCustomerInformation extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    session: PropTypes.instanceOf(Session).isRequired,
    sectionNumber: PropTypes.number.isRequired,
    number: PropTypes.number.isRequired,
    prevState: PropTypes.object,
    config: PropTypes.object,
    onChange: PropTypes.func.isRequired
  };

  static companyExclusive = false;
  static privateExclusive = false;
  static fields = _getFields();

  urlParameters = Object.fromEntries(
    new URLSearchParams(window.location.search)
  );
  state = _getInitialState.call(this);

  componentDidUpdate(prevProps, prevState) {
    const {props, state} = this;
    const {id, onChange} = props;

    if (prevState.fields !== state.fields) {
      onChange(id, {...state}, fulfillmentCheck(props, state));
    }
  }

  componentDidMount() {
    if (this.urlParameters.hasOwnProperty("ssn")) {
      Validator.validateSsn(this.urlParameters.ssn, true);
      this._onFieldChange(
        "customer_social_security_number",
        this.urlParameters.ssn
      );
    }
  }

  render() {
    const {props, state} = this;
    const {title} = props;
    const classes = createClassName("Cob-CustomerInformation", {
      "Cob-View": true,
      fulfilled: state.isFulfilled
    });

    const langCode = Settings.get("LANGUAGE");
    let ssnShort =
      langCode === "fi"
        ? state.fields.customer_social_security_number
        : state.fields.customer_social_security_number.replaceAll("-", "");

    let isCompany = false;
    if (props.config && props.config.company_only) {
      isCompany = true;
    } else {
      isCompany =
        !Validator.validateSsn(ssnShort, true) &&
        Validator.validateOrgNum(ssnShort, true);
      if (ssnShort.length > 10) {
        ssnShort = ssnShort.substring(2, ssnShort.length);
      }
    }

    const isFetchButtonDisabled = isCompany
      ? !Validator.validateOrgNum(
          state.fields.customer_social_security_number,
          true
        )
      : !Validator.validateSsn(
          state.fields.customer_social_security_number,
          true
        );

    return (
      <div className={classes}>
        <DefaultFields fields={this.constructor.fields} />

        <input
          type="hidden"
          name="house_comp_name"
          value="GlofiTech Solutions AB"
        />
        <input
          type="hidden"
          name="house_address_street"
          value="Kungsgatan 37"
        />
        <input type="hidden" name="house_address_zipcode" value="111 56" />
        <input type="hidden" name="house_address_city" value="Stockholm" />
        <input type="hidden" name="house_comp_no" value="559078-3477" />
        <input type="hidden" name="house_bgno" value="1234-5678" />

        <div className="Cob-Fields">
          <h2>{title}</h2>

          <FieldsCollection
            fieldsPerRow={2}
            fields={[
              ...(!state.prePopulatedFields
                ? [
                    {
                      render: (
                        <EditableField
                          name="customer_social_security_number"
                          label={i18n("customer_info", "ssn")}
                          placeholder={i18n("customer_info", "ssn_format")}
                          value={state.fields.customer_social_security_number}
                          onChange={this._onFieldChange}
                          onBlur={this._onFieldBlur}
                          required
                        />
                      )
                    },
                    {
                      render: (
                        <div style={{height: "100%", justifyContent: "center"}}>
                          <Button
                            label={i18n("customer_info", "fetch_cta")}
                            appearance="success"
                            disabled={isFetchButtonDisabled}
                            busy={state.busy}
                            onClick={this._fetchCustomerDetails}
                          />
                        </div>
                      )
                    }
                  ]
                : [
                    {
                      render: (
                        <EditableField
                          name="customer_social_security_number"
                          label={i18n("customer_info", "ssn")}
                          placeholder={i18n("customer_info", "ssn_format")}
                          value={state.fields.customer_social_security_number}
                          onChange={this._onFieldChange}
                          onBlur={this._onFieldBlur}
                          required
                        />
                      )
                    }
                  ])
            ]}
          />
          {!state.prePopulatedFields && (
            <p style={{padding: "0 5px"}}>
              {i18n("customer_info", "fill_manually_or")}
              <span className="link" onClick={this._onUnlock}>
                {i18n("customer_info", "fill_manually_action")}
              </span>
            </p>
          )}

          {isCompany && (
            <FieldsCollection
              fieldsPerRow={2}
              fields={[
                {
                  render: (
                    <EditableField
                      name="customer_firstname"
                      label={i18n("customer_info", "business_name")}
                      value={state.fields.customer_firstname}
                      valid={state.fields.customer_firstname.length > 0}
                      onChange={this._onFieldChange}
                      onBlur={this._onFieldBlur}
                      readOnly={state.fieldsLocked}
                      className={
                        state.fields.customer_firstname.trim().length === 0
                          ? "empty"
                          : ""
                      }
                      required
                    />
                  )
                }
              ]}
            />
          )}

          {!isCompany && (
            <FieldsCollection
              fieldsPerRow={2}
              fields={[
                {
                  render: (
                    <EditableField
                      name="customer_firstname"
                      label={"Förnamn"}
                      value={state.fields.customer_firstname}
                      valid={state.fields.customer_firstname.length > 0}
                      onChange={this._onFieldChange}
                      onBlur={this._onFieldBlur}
                      readOnly={state.fieldsLocked}
                      className={
                        state.fields.customer_firstname.trim().length === 0
                          ? "empty"
                          : ""
                      }
                      required
                    />
                  )
                },
                {
                  render: (
                    <EditableField
                      name="customer_lastname"
                      label="Efternamn"
                      value={state.fields.customer_lastname}
                      valid={state.fields.customer_lastname.length > 0}
                      onChange={this._onFieldChange}
                      onBlur={this._onFieldBlur}
                      readOnly={state.fieldsLocked}
                      className={
                        state.fields.customer_lastname.trim().length === 0
                          ? "empty"
                          : ""
                      }
                      required
                    />
                  )
                }
              ]}
            />
          )}

          <input
            type="hidden"
            name="customer_name"
            value={
              state.fields.customer_firstname +
              (isCompany ? "" : " " + state.fields.customer_lastname)
            }
          />

          <input
            type="hidden"
            name="customer_autogiro_custno"
            value={state.fields.customer_social_security_number}
          />

          <input
            type="hidden"
            name="customer_social_security_number"
            value={ssnShort}
          />

          <FieldsCollection
            fieldsPerRow={3}
            fields={[
              {
                render: (
                  <EditableField
                    name="customer_address_street"
                    label={i18n("customer_info", "address")}
                    value={state.fields.customer_address_street}
                    valid={state.fields.customer_address_street.length > 0}
                    onChange={this._onFieldChange}
                    onBlur={this._onFieldBlur}
                    readOnly={state.fieldsLocked}
                    className={
                      state.fields.customer_address_street.trim().length === 0
                        ? "empty"
                        : ""
                    }
                    required
                  />
                )
              },
              {
                render: (
                  <EditableField
                    name="customer_address_zipcode"
                    label={i18n("customer_info", "zipcode")}
                    value={state.fields.customer_address_zipcode}
                    valid={state.fields.customer_address_zipcode.length > 0}
                    onChange={this._onFieldChange}
                    onBlur={this._onFieldBlur}
                    readOnly={state.fieldsLocked}
                    className={
                      state.fields.customer_address_zipcode.trim().length === 0
                        ? "empty"
                        : ""
                    }
                    required
                  />
                )
              },
              {
                render: (
                  <EditableField
                    name="customer_address_city"
                    label={i18n("customer_info", "city")}
                    value={state.fields.customer_address_city}
                    valid={state.fields.customer_address_city.length > 0}
                    onChange={this._onFieldChange}
                    onBlur={this._onFieldBlur}
                    readOnly={state.fieldsLocked}
                    className={
                      state.fields.customer_address_city.trim().length === 0
                        ? "empty"
                        : ""
                    }
                    required
                  />
                )
              }
            ]}
          />

          {state.prePopulatedFields && (
            <p style={{padding: "0 5px"}}>
              <span className="link" onClick={this._onUnlock}>
                {capitalizeFirstLetter(
                  i18n("customer_info", "fill_manually_action")
                )}
              </span>
            </p>
          )}

          {isCompany && (
            <CompanyReps
              orgNr={state.fields.customer_social_security_number}
              firma={state.fields.company_firma}
              ceo={state.fields.company_ceo}
              board={state.fields.company_board}
              onRepsUpdated={this._onRepsUpdated}
            />
          )}

          {!isCompany && (
            <React.Fragment>
              <p style={{padding: "0 5px", marginTop: "30px"}}>
                {i18n("customer_info", "email_info")}
              </p>

              <FieldsCollection
                fieldsPerRow={2}
                fields={[
                  {
                    render: (
                      <EditableField
                        type="email"
                        name="customer_email"
                        label={i18n("customer_info", "email")}
                        value={state.fields.customer_email}
                        valid={state.validFields.customer_email}
                        onChange={this._onFieldChange}
                        onBlur={this._onFieldBlur}
                        required
                      />
                    )
                  }
                ]}
              />
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }

  // Internal methods
  _fieldValidator = (name, value) => {
    if (name === "customer_social_security_number") {
      return Validator.validateSsn(value, true);
    } else if (name === "customer_email") {
      return Validator.validateEmail(value);
    } else {
      return value.length > 0;
    }
  };

  _onFieldChange = (name, value) => {
    CobHelpers.onViewFieldChange.call(this, name, value);
  };

  _onUnlock = () => {
    this.setState({fieldsLocked: false});
  };

  _fetchCustomerDetails = async () => {
    this.setState({busy: true}, async () => {
      let lookupData = null;

      let isCompany =
        !Validator.validateSsn(
          this.state.fields.customer_social_security_number,
          true
        ) &&
        Validator.validateOrgNum(
          this.state.fields.customer_social_security_number,
          true
        );

      try {
        lookupData = await api("/lookup", {
          type: isCompany ? "company" : "person",
          value: this.state.fields.customer_social_security_number
        });
      } catch (e) {
        console.log(e);
      }

      if (lookupData == null) {
        // Show error
        this.setState({busy: false, fieldsLocked: false});
        return;
      }

      const fieldsData = this.state.fields;
      fieldsData.customer_address_street = lookupData.street;
      fieldsData.customer_address_zipcode = lookupData.zipCode;
      fieldsData.customer_address_city = lookupData.city;

      if (isCompany) {
        fieldsData.customer_firstname = lookupData.companyName;
        fieldsData.customer_lastname = "";

        fieldsData.company_firma = lookupData.registeredFirmSignee;
        fieldsData.company_ceo = lookupData.ceo;
        fieldsData.company_board = lookupData.board;
      } else {
        fieldsData.customer_firstname = lookupData.firstName;
        fieldsData.customer_lastname = lookupData.lastName;
        fieldsData.company_firma = "";
        fieldsData.company_ceo = null;
        fieldsData.company_board = [];
      }

      this.setState({busy: false, fieldsLocked: false, fields: fieldsData});
    });
  };

  _onFieldBlur = name => {
    CobHelpers.onViewFieldBlur.call(this, name);
  };

  _onRepsUpdated = reps => {
    this.setState({fields: {...this.state.fields, reps: reps}});
  };
}

// PRIVATE FUNCTIONS
function _getFields() {
  return [
    "customer_firstname",
    "customer_lastname",
    "customer_social_security_number",
    "customer_address_street",
    "customer_address_zipcode",
    "customer_address_city",
    "customer_email"
  ];
}

function _getInitialState() {
  if (this.props.prevState) return {...this.props.prevState};

  return {
    busy: false,
    fieldsLocked: true,
    prePopulatedFields: this.urlParameters.hasOwnProperty("ssn"),
    validFields: {},
    fields: {
      customer_firstname: this.urlParameters.firstName || "",
      customer_lastname: this.urlParameters.lastName || "",
      customer_social_security_number: this.urlParameters.ssn || "",
      customer_address_street: this.urlParameters.address1 || "",
      customer_address_zipcode: this.urlParameters.zipCode || "",
      customer_address_city: this.urlParameters.city || "",
      customer_email: this.urlParameters.email || "",

      company_firma: "",
      company_ceo: null,
      company_board: [],
      reps: [],

      customer_type: {
        options: [
          {label: "Privatperson", value: "privatperson", checked: false},
          {label: "Företag", value: "foretag", checked: false}
        ]
      }
    }
  };
}



// WEBPACK FOOTER //
// ./src/routes/Cob/views/CustomerInformation/no-prefetch/CustomerInformation.js